import { useContext } from 'react';
import { Tree, TreeItem } from '@origo/origo-ui';
import type { Område } from '@origo/seksjon';
import { FolderSimpleIcon } from '@phosphor-icons/react';
import { formatAreal } from '../../planer/utils/planUtils';
import { NavDragContext } from './NavPanelDragContext';
import type { OmrådeDragTarget } from './NavPanelDragContext';
import type { DelområdegruppeView } from './NavPanel';
import styles from './NavPanel.module.css';

/* ------------------------------------------------------------------ */
/*  DelomradetypeNode                                                   */
/* ------------------------------------------------------------------ */

interface DelomradetypeNodeProps {
    area: Område;
    grupperForType: readonly DelområdegruppeView[];
    memberToGruppe: Map<string, string>;
    egendefinertByDelområde: Map<string, Område[]>;
    egendefinertForType: readonly Område[];
}

export function DelomradetypeNode({
    area,
    grupperForType,
    memberToGruppe,
    egendefinertByDelområde,
    egendefinertForType,
}: Readonly<DelomradetypeNodeProps>) {
    const drag = useContext(NavDragContext);
    const childCount =
        (area.children?.length ?? 0) + grupperForType.length + egendefinertForType.length;
    const ungroupedChildren = (area.children ?? []).filter((c) => !memberToGruppe.has(c.id));

    const dragTarget: OmrådeDragTarget = { parentType: 'Delområdetype', parentId: area.id, navn: area.navn };

    return (
        <TreeItem
            id={area.id}
            code={area.code}
            label={area.navn}
            count={childCount}
            forceExpanded={drag.forceExpandedIds.has(area.id) ? true : undefined}
            dragOver={drag.isDragEnabled && (drag.dragOverId === area.id || drag.dropTargetId === area.id)}
            dragProps={drag.isDragEnabled ? {
                onDragEnter: (e) => drag.onTargetDragEnter(area.id, e),
                onDragOver: (e) => drag.onTargetDragOver(dragTarget, e),
                onDragLeave: (e) => drag.onTargetDragLeave(area.id, e),
                onDrop: (e) => drag.onTargetDrop(dragTarget, e),
            } : undefined}
        >
            {grupperForType.map((g) => (
                <TreeItem
                    key={`gruppe:${g.id}`}
                    id={`gruppe:${g.id}`}
                    label={g.navn}
                    icon={<FolderSimpleIcon size={16} weight="duotone" />}
                    count={g.memberDelomradeIds.length}
                    forceExpanded={drag.forceExpandedIds.has(`gruppe:${g.id}`) ? true : undefined}
                >
                    {(area.children ?? [])
                        .filter((c) => g.memberDelomradeIds.includes(c.id))
                        .map((member) => renderDelområdeNode(member, area.id, egendefinertByDelområde))}
                </TreeItem>
            ))}
            {ungroupedChildren.map((child) => renderDelområdeNode(child, area.id, egendefinertByDelområde))}
            {egendefinertForType.map((egendef) => (
                <DraggableEgendefinertItem key={egendef.id} omrade={egendef} />
            ))}
        </TreeItem>
    );
}

/* ------------------------------------------------------------------ */
/*  Draggable egendefinert leaf node                                    */
/* ------------------------------------------------------------------ */

export function DraggableEgendefinertItem({ omrade }: { omrade: Område }) {
    const drag = useContext(NavDragContext);
    return (
        <TreeItem
            id={omrade.id}
            label={omrade.navn}
            dragProps={drag.isDragEnabled ? {
                draggable: true,
                onDragStart: (e) => drag.onEgendefinertDragStart(omrade, e),
                onDragEnd: (e) => drag.onEgendefinertDragEnd(e),
            } : undefined}
        />
    );
}

/* ------------------------------------------------------------------ */
/*  renderDelområdeNode                                                 */
/* ------------------------------------------------------------------ */

function renderDelområdeNode(
    delområde: Område,
    delomradetype: string,
    egendefinertByDelområde: Map<string, Område[]>,
): JSX.Element {
    const egendefinerte = egendefinertByDelområde.get(delområde.id) ?? [];
    const areal = formatAreal(delområde.areaM2);
    const meta = areal ? `(${areal})` : undefined;

    return (
        <DelomradeNodeWithDrag
            key={delområde.id}
            delområde={delområde}
            delomradetype={delomradetype}
            egendefinerte={egendefinerte}
            meta={meta}
        />
    );
}

interface DelomradeNodeWithDragProps {
    delområde: Område;
    delomradetype: string;
    egendefinerte: Område[];
    meta?: string;
}

function DelomradeNodeWithDrag({ delområde, delomradetype, egendefinerte, meta }: DelomradeNodeWithDragProps) {
    const drag = useContext(NavDragContext);
    const dragTarget: OmrådeDragTarget = {
        parentType: 'Delområde',
        parentId: delområde.id,
        navn: delområde.code ? `${delområde.code} ${delområde.navn}` : delområde.navn,
        delomradetype,
    };

    if (egendefinerte.length === 0) {
        const dropHandlers = drag.isDragEnabled ? {
            onDragEnter: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDragEnter(delområde.id, e),
            onDragOver: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDragOver(dragTarget, e),
            onDragLeave: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDragLeave(delområde.id, e),
            onDrop: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDrop(dragTarget, e),
        } : undefined;
        return (
            <TreeItem
                id={delområde.id}
                code={delområde.code}
                label={delområde.navn}
                meta={meta}
                forceExpanded={drag.forceExpandedIds.has(delområde.id) ? true : undefined}
                dragOver={drag.isDragEnabled && (drag.dragOverId === delområde.id || drag.dropTargetId === delområde.id)}
                dragProps={dropHandlers}
            />
        );
    }
    const dropHandlers = drag.isDragEnabled ? {
        onDragEnter: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDragEnter(delområde.id, e),
        onDragOver: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDragOver(dragTarget, e),
        onDragLeave: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDragLeave(delområde.id, e),
        onDrop: (e: React.DragEvent<HTMLDivElement>) => drag.onTargetDrop(dragTarget, e),
    } : undefined;
    return (
        <TreeItem
            id={delområde.id}
            code={delområde.code}
            label={delområde.navn}
            meta={meta}
            count={egendefinerte.length}
            forceExpanded={drag.forceExpandedIds.has(delområde.id) ? true : undefined}
            dragOver={drag.isDragEnabled && (drag.dragOverId === delområde.id || drag.dropTargetId === delområde.id)}
            dragProps={dropHandlers}
        >
            {egendefinerte.map((item) => (
                <DraggableEgendefinertItem key={item.id} omrade={item} />
            ))}
        </TreeItem>
    );
}

/* ------------------------------------------------------------------ */
/*  renderEgendefinertItem — egendefinerte-treet (Plan-parent)          */
/* ------------------------------------------------------------------ */

export function renderEgendefinertItem(item: Område): JSX.Element {
    const hasChildren = item.children != null && item.children.length > 0;
    return (
        <DraggableEgendefinertItemWithChildren
            key={item.id}
            item={item}
            hasChildren={hasChildren}
        />
    );
}

function DraggableEgendefinertItemWithChildren({
    item,
    hasChildren,
}: {
    item: Område;
    hasChildren: boolean;
}) {
    const drag = useContext(NavDragContext);
    return (
        <TreeItem
            id={item.id}
            label={item.navn}
            dragProps={drag.isDragEnabled ? {
                draggable: true,
                onDragStart: (e) => drag.onEgendefinertDragStart(item, e),
                onDragEnd: (e) => drag.onEgendefinertDragEnd(e),
            } : undefined}
        >
            {hasChildren ? item.children?.map(renderEgendefinertItem) : undefined}
        </TreeItem>
    );
}

/* ------------------------------------------------------------------ */
/*  EgendefinertTree — Tree wrapper for the egendefinerte section       */
/* ------------------------------------------------------------------ */

interface EgendefinertTreeProps {
    egendefinertTree: Område[];
    activeOmradeId: string;
    onSelect: (item: Område) => void;
    label: string;
}

export function EgendefinertTree({ egendefinertTree, activeOmradeId, onSelect, label }: EgendefinertTreeProps) {
    function find(items: Område[], id: string): Område | undefined {
        for (const item of items) {
            if (item.id === id) return item;
            const child = find(item.children ?? [], id);
            if (child) return child;
        }
    }

    return (
        <section className={styles.treeRegion} aria-label={label}>
            <Tree
                activeId={activeOmradeId}
                onSelect={(id) => {
                    const found = find(egendefinertTree, id);
                    if (found) onSelect(found);
                }}
            >
                {egendefinertTree.map((item) => renderEgendefinertItem(item))}
            </Tree>
        </section>
    );
}
